/**
 * The GDV figure with your own value: shows your saved GDV when you have one,
 * and (with settings write access) a pencil that edits or clears it. The
 * system estimate and its evidence range are never changed by this.
 */
import { useEffect, useState } from 'react'
import { IconPencil } from '@tabler/icons-react'
import { ApiError } from '../../../api/http'
import { useAccess } from '../../../api/queries'
import { poundsToPence, usePatchPersonalGdv, usePersonalSettings } from '../../../api/diligence'
import type { PersonalSettings } from '../../../api/types'
import { Alert, Button, Field, IconButton, NumberInput, Popover } from '../../../ui/kit'
import { money } from './format'
import { useEscapeFirst } from './ui'

const exact = (pence: number) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(pence / 100)

export function GdvValue({ propertyId, systemGdvPence, snapshot, compact = false }: { propertyId: string; systemGdvPence: number | null | undefined; snapshot: PersonalSettings | null | undefined; compact?: boolean }) {
  // Without a personal snapshot the value is inert, exactly the system estimate.
  return snapshot ? <ConnectedGdv propertyId={propertyId} systemGdvPence={systemGdvPence} snapshot={snapshot} compact={compact} /> : <>{money(systemGdvPence)}</>
}

function ConnectedGdv({ propertyId, systemGdvPence, snapshot, compact }: { propertyId: string; systemGdvPence: number | null | undefined; snapshot: PersonalSettings; compact: boolean }) {
  const access = useAccess()
  const canRead = access.capability('workspace.settings.read') && access.capability('property.valuation.read')
  const canEdit = canRead && access.capability('workspace.settings.write')
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState('')
  useEscapeFirst(open, () => setOpen(false))
  const query = usePersonalSettings(propertyId, snapshot, open && canRead)
  const save = usePatchPersonalGdv(propertyId)
  const { reset } = save
  useEffect(() => { setOpen(false); setDraft(''); reset() }, [propertyId, reset])
  const current = query.data ?? snapshot
  const own = canRead ? current.gdvOverridePence : null
  const parsed = poundsToPence(draft)
  const valid = parsed !== null && parsed > 0
  const showEditor = () => {
    reset()
    const amount = own ?? systemGdvPence
    setDraft(amount == null ? '' : `${Math.floor(amount / 100)}.${String(amount % 100).padStart(2, '0')}`)
    setOpen(true)
  }
  const write = (gdvOverridePence: number | null) => {
    if (!canEdit || save.isPending) return
    save.mutate({ gdvOverridePence, expectedGdvRevision: current.gdvRevision }, { onSuccess: () => setOpen(false) })
  }
  return (
    <span className="dil-gdv">
      <span>{own !== null ? exact(own) : money(systemGdvPence)}</span>
      {own !== null && !compact ? <span className="dil-gdv-own">Your value</span> : null}
      {canEdit ? (
        <Popover open={open} onClose={() => setOpen(false)} width={310} anchor={<IconButton small label="Edit your GDV" onClick={() => (open ? setOpen(false) : showEditor())}><IconPencil className="ic" /></IconButton>}>
          <form className="dil-gdv-form" onSubmit={event => { event.preventDefault(); if (valid) write(parsed) }}>
            <div className="pop-title">Your GDV</div>
            <p className="t-muted">Applies to your calculations for this property.</p>
            <p>System estimate: {money(systemGdvPence)}</p>
            {own !== null ? <p>Your saved GDV: {exact(own)}</p> : null}
            <Field label="GDV (£)" error={!valid ? 'Enter a positive amount with at most two decimal places.' : undefined}>
              <NumberInput value={draft} onChange={value => { setDraft(value); reset() }} prefix="£" ariaLabel="Your GDV in pounds" invalid={!valid} />
            </Field>
            {query.isError ? <Alert tight title="Current settings unavailable">{query.error.message}</Alert> : null}
            {save.isError ? <Alert tight title={save.error instanceof ApiError && save.error.status === 409 ? 'Your GDV changed elsewhere' : 'GDV could not be saved'}>{save.error.message}</Alert> : null}
            <div className="form-row dil-gdv-acts">
              <Button small disabled={own === null || save.isPending || query.isError} onClick={() => write(null)}>Use system estimate</Button>
              <Button small kind="primary" type="submit" busy={save.isPending} disabled={!valid || query.isError || query.isFetching}>Save</Button>
            </div>
          </form>
        </Popover>
      ) : null}
    </span>
  )
}
